import { zodResolver } from "@hookform/resolvers/zod";
import { loginRequestSchema, mfaCodeSchema } from "@station/contracts";
import { Alert, Button, FormField, Input } from "@station/ui";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AuthLayout } from "../components/auth-layout.js";
import { readAppConfig } from "../config.js";
import { DemoLanding } from "../features/demo/demo-landing.js";
import { errorMessage, isApiError } from "../lib/errors.js";
import { bootstrapSession, login, verifyMfa } from "../lib/session.js";

const searchSchema = z.object({ redirect: z.string().startsWith("/").optional() });

export const Route = createFileRoute("/giris")({
  validateSearch: searchSchema,
  // Zaten oturumu olan kullanıcı giriş ekranını görmez.
  beforeLoad: async ({ search }) => {
    if (await bootstrapSession()) throw redirect({ to: search.redirect ?? "/" });
  },
  component: LoginPage,
});

const credentialsSchema = loginRequestSchema.pick({ email: true, password: true });
type Credentials = z.input<typeof credentialsSchema>;
const codeSchema = z.object({ code: mfaCodeSchema });
/** Demo kipi (ADR 0026): tanımlıysa giriş ekranı rol kartlarını ve kullanım kataloğunu gösterir. */
const demo = readAppConfig().demo;

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { redirect: redirectTo } = Route.useSearch();
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // Demo kipinde de e-posta / parola formuna geçilebilir (ör. iki adımlı doğrulaması açık hesap).
  const [classic, setClassic] = useState(false);

  const credentials = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: "", password: "" },
  });
  const mfa = useForm<{ code: string }>({ resolver: zodResolver(codeSchema), defaultValues: { code: "" } });
  const done = () => navigate({ to: redirectTo ?? "/" });

  const submitCredentials = credentials.handleSubmit(async (values) => {
    setFormError(null);
    try {
      const result = await login({
        ...values,
        deviceLabel: navigator.userAgent.includes("Mobile")
          ? t("auth.login.deviceMobile")
          : t("auth.login.deviceBrowser"),
      });
      if (result.status === "MFA_REQUIRED") setMfaToken(result.mfaToken);
      else await done();
    } catch (error) {
      setFormError(errorMessage(error));
      credentials.setFocus("password");
    }
  });

  const submitCode = mfa.handleSubmit(async ({ code }) => {
    setFormError(null);
    try {
      await verifyMfa(mfaToken!, code);
      await done();
    } catch (error) {
      // Adım token'ının süresi dolduysa baştan başlanır.
      if (isApiError(error, "TOKEN_INVALID")) setMfaToken(null);
      setFormError(errorMessage(error));
      mfa.setFocus("code");
    }
  });

  if (mfaToken) {
    return (
      <AuthLayout title={t("auth.mfa.title")} description={t("auth.mfa.description")}>
        <form onSubmit={(event) => void submitCode(event)} className="grid gap-4" noValidate>
          {formError ? <Alert tone="danger">{formError}</Alert> : null}
          <FormField
            label={t("auth.mfa.code")}
            description={t("auth.mfa.hint")}
            error={mfa.formState.errors.code?.message}
            required
          >
            {(control) => (
              <Input
                {...control}
                {...mfa.register("code")}
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                className="text-center text-lg tracking-widest"
              />
            )}
          </FormField>
          <Button type="submit" loading={mfa.formState.isSubmitting}>
            {t("auth.mfa.submit")}
          </Button>
          <Button variant="ghost" onClick={() => (setMfaToken(null), setFormError(null))}>
            {t("auth.mfa.back")}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  if (demo && !classic) {
    return (
      <DemoLanding password={demo.password} redirectTo={redirectTo} onClassicLogin={() => setClassic(true)} />
    );
  }

  return (
    <AuthLayout
      title={demo ? t("demo.classic.title") : t("auth.login.title")}
      description={t("auth.login.description")}
      footer={
        demo ? (
          <button
            type="button"
            onClick={() => (setClassic(false), setFormError(null))}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {t("demo.classic.back")}
          </button>
        ) : undefined
      }
    >
      <form onSubmit={(event) => void submitCredentials(event)} className="grid gap-4" noValidate>
        {formError ? <Alert tone="danger">{formError}</Alert> : null}
        <FormField label={t("auth.email")} error={credentials.formState.errors.email?.message} required>
          {(control) => (
            <Input
              {...control}
              {...credentials.register("email")}
              type="email"
              autoComplete="username"
              autoFocus
            />
          )}
        </FormField>
        <FormField label={t("auth.password")} error={credentials.formState.errors.password?.message} required>
          {(control) => (
            <Input
              {...control}
              {...credentials.register("password")}
              type="password"
              autoComplete="current-password"
            />
          )}
        </FormField>
        <Button type="submit" loading={credentials.formState.isSubmitting}>
          {t("auth.login.submit")}
        </Button>
        <Link
          to="/parola-unuttum"
          className="text-center text-sm text-primary underline-offset-4 hover:underline"
        >
          {t("auth.login.forgot")}
        </Link>
      </form>
    </AuthLayout>
  );
}
